import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/users';
import { useAuthStore } from '@/store/authStore';
import { Trash2, Hash, Lock, Pencil, Star, X, Check } from 'lucide-react';
import { format } from 'date-fns';

type AdminChannel = {
  id: string;
  name: string;
  displayName: string;
  type: string;
  topic: string | null;
  description: string | null;
  isDefault: boolean;
  mediaEnabled: boolean;
  reactionsEnabled: boolean;
  membersCanPost: boolean;
  chatStyle?: 'BUBBLE' | 'CLASSIC';
  createdAt: string;
  _count: { members: number; messages: number };
};

interface EditDraft {
  id: string;
  displayName: string;
  topic: string;
  description: string;
  mediaEnabled: boolean;
  reactionsEnabled: boolean;
  membersCanPost: boolean;
  chatStyle: 'BUBBLE' | 'CLASSIC';
}

export default function AdminChannels() {
  const { user } = useAuthStore();
  const isSuperAdmin = user?.globalRole === 'SUPER_ADMIN';
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const [onlyDefault, setOnlyDefault] = useState(false);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-channels', search, cursor],
    queryFn: () => adminApi.listChannels({ search, cursor, limit: 100 }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => adminApi.deleteChannel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-channels'] }),
  });

  const setDefaultMut = useMutation({
    mutationFn: ({ id, isDefault }: { id: string; isDefault: boolean }) =>
      adminApi.setChannelDefault(id, isDefault),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-channels'] }),
    onError: (e: any) => alert(e?.response?.data?.message ?? 'Не удалось'),
  });

  const updateMut = useMutation({
    mutationFn: (payload: EditDraft) => {
      const { id, ...rest } = payload;
      return adminApi.updateChannelAsAdmin(id, {
        displayName: rest.displayName.trim(),
        topic: rest.topic.trim() || null,
        description: rest.description.trim() || null,
        mediaEnabled: rest.mediaEnabled,
        reactionsEnabled: rest.reactionsEnabled,
        membersCanPost: rest.membersCanPost,
        chatStyle: rest.chatStyle,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-channels'] });
      setEditDraft(null);
      setEditError(null);
    },
    onError: (e: any) => setEditError(e?.response?.data?.message ?? 'Ошибка сохранения'),
  });

  const allChannels: AdminChannel[] = (data as { data: AdminChannel[] })?.data ?? [];
  const channels = onlyDefault ? allChannels.filter((c) => c.isDefault) : allChannels;

  const startEdit = (c: AdminChannel) => {
    setEditDraft({
      id: c.id,
      displayName: c.displayName ?? '',
      topic: c.topic ?? '',
      description: c.description ?? '',
      mediaEnabled: c.mediaEnabled ?? true,
      reactionsEnabled: c.reactionsEnabled ?? true,
      membersCanPost: c.membersCanPost ?? true,
      chatStyle: (c.chatStyle as 'BUBBLE' | 'CLASSIC') ?? 'BUBBLE',
    });
    setEditError(null);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-surface-50">Каналы</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-xs text-surface-300 cursor-pointer">
            <input
              type="checkbox"
              checked={onlyDefault}
              onChange={(e) => setOnlyDefault(e.target.checked)}
              className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500"
            />
            Только default-каналы
          </label>
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setCursor(undefined); }}
            className="irc-input w-56"
            placeholder="Поиск каналов…"
          />
        </div>
      </div>

      {!isSuperAdmin && (
        <div className="mb-4 bg-amber-900/30 border border-amber-800/50 text-amber-300 text-xs rounded-lg px-3 py-2">
          Управление <strong>default</strong> (авто-подписка новых юзеров) доступно только SUPER_ADMIN.
          Редактирование полей работает для всех админов.
        </div>
      )}

      {isLoading ? (
        <div className="text-surface-500">Загрузка…</div>
      ) : (
        <div className="bg-surface-900 border border-surface-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-800">
                <th className="text-left px-4 py-3 text-surface-400 font-medium">Канал</th>
                <th className="text-left px-4 py-3 text-surface-400 font-medium">Тип</th>
                <th className="text-center px-2 py-3 text-surface-400 font-medium">Default</th>
                <th className="text-center px-2 py-3 text-surface-400 font-medium">Style</th>
                <th className="text-center px-2 py-3 text-surface-400 font-medium">Медиа</th>
                <th className="text-center px-2 py-3 text-surface-400 font-medium">Постят</th>
                <th className="text-right px-3 py-3 text-surface-400 font-medium">Чел.</th>
                <th className="text-right px-3 py-3 text-surface-400 font-medium">Сообщ.</th>
                <th className="text-left px-4 py-3 text-surface-400 font-medium">Создан</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => (
                <tr key={c.id} className="border-b border-surface-800/50 hover:bg-surface-800/30">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {c.type === 'PRIVATE' ? (
                        <Lock size={13} className="text-surface-500" />
                      ) : (
                        <Hash size={13} className="text-surface-500" />
                      )}
                      <div>
                        <p className="font-medium text-surface-100">{c.displayName}</p>
                        <p className="text-surface-500 text-xs font-mono">{c.name}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-surface-400 text-xs font-mono">{c.type}</td>
                  <td className="px-2 py-2.5 text-center">
                    <button
                      onClick={() => {
                        if (!isSuperAdmin) return;
                        setDefaultMut.mutate({ id: c.id, isDefault: !c.isDefault });
                      }}
                      disabled={!isSuperAdmin || setDefaultMut.isPending}
                      title={
                        c.isDefault
                          ? 'Убрать из default (новые юзеры не будут сюда автоматически подписываться)'
                          : 'Сделать default (все текущие и новые юзеры будут подписаны)'
                      }
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors ${c.isDefault ? 'text-amber-400 bg-amber-900/30 hover:bg-amber-900/60' : 'text-surface-500 hover:text-amber-400 hover:bg-surface-800'} ${!isSuperAdmin ? 'opacity-30 cursor-not-allowed' : ''}`}
                    >
                      <Star size={14} fill={c.isDefault ? 'currentColor' : 'none'} />
                    </button>
                  </td>
                  <td className="px-2 py-2.5 text-center text-[10px] text-surface-400">
                    {c.chatStyle === 'CLASSIC' ? (
                      <span className="inline-block bg-purple-900/40 text-purple-300 px-1.5 rounded">IRC</span>
                    ) : (
                      <span className="inline-block bg-surface-800 text-surface-500 px-1.5 rounded">BUBBLE</span>
                    )}
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {c.mediaEnabled ? <Check size={13} className="inline text-emerald-400" /> : <X size={13} className="inline text-red-400" />}
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    {c.membersCanPost ? <Check size={13} className="inline text-emerald-400" /> : <X size={13} className="inline text-amber-400" />}
                  </td>
                  <td className="px-3 py-2.5 text-right text-surface-300">{c._count?.members ?? 0}</td>
                  <td className="px-3 py-2.5 text-right text-surface-300">{c._count?.messages ?? 0}</td>
                  <td className="px-4 py-2.5 text-surface-500 text-xs">
                    {format(new Date(c.createdAt), 'MMM d, yyyy')}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <button
                      onClick={() => startEdit(c)}
                      title="Редактировать"
                      className="btn-ghost text-xs px-1.5 py-1 text-surface-400 hover:text-brand-400"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`Удалить канал ${c.name}?`)) deleteMut.mutate(c.id);
                      }}
                      className="btn-ghost text-xs px-1.5 py-1 text-red-400"
                      title="Удалить"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
              {channels.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-surface-500 py-6">
                    {onlyDefault ? 'Нет default-каналов' : 'Каналы не найдены'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit modal */}
      {editDraft && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setEditDraft(null)}
        >
          <div
            className="bg-surface-900 border border-surface-700 rounded-xl w-full max-w-lg p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-surface-100 font-semibold">Редактировать канал</h3>
              <button onClick={() => setEditDraft(null)} className="p-1 text-surface-400 hover:text-surface-200">
                <X size={16} />
              </button>
            </div>

            {editError && (
              <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded px-3 py-2">
                {editError}
              </div>
            )}

            <div>
              <label className="text-surface-400 text-xs block mb-1">Отображаемое имя</label>
              <input
                value={editDraft.displayName}
                onChange={(e) => setEditDraft({ ...editDraft, displayName: e.target.value })}
                className="w-full bg-surface-800 border border-surface-700 rounded px-3 py-1.5 text-surface-100 text-sm"
              />
            </div>

            <div>
              <label className="text-surface-400 text-xs block mb-1">Тема</label>
              <input
                value={editDraft.topic}
                onChange={(e) => setEditDraft({ ...editDraft, topic: e.target.value })}
                className="w-full bg-surface-800 border border-surface-700 rounded px-3 py-1.5 text-surface-100 text-sm"
              />
            </div>

            <div>
              <label className="text-surface-400 text-xs block mb-1">Описание</label>
              <textarea
                value={editDraft.description}
                onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                rows={2}
                className="w-full bg-surface-800 border border-surface-700 rounded px-3 py-1.5 text-surface-100 text-sm"
              />
            </div>

            <div>
              <label className="text-surface-400 text-xs block mb-1">Стиль чата</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setEditDraft({ ...editDraft, chatStyle: 'BUBBLE' })}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-sm border ${editDraft.chatStyle === 'BUBBLE' ? 'bg-brand-600 text-white border-brand-600' : 'bg-surface-800 text-surface-300 border-surface-700'}`}
                >Пузырьки</button>
                <button
                  type="button"
                  onClick={() => setEditDraft({ ...editDraft, chatStyle: 'CLASSIC' })}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-sm border ${editDraft.chatStyle === 'CLASSIC' ? 'bg-brand-600 text-white border-brand-600' : 'bg-surface-800 text-surface-300 border-surface-700'}`}
                >IRC Классик</button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editDraft.mediaEnabled}
                onChange={(e) => setEditDraft({ ...editDraft, mediaEnabled: e.target.checked })}
                className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500"
              />
              <span className="text-surface-200 text-sm">Разрешить загрузку фото</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editDraft.reactionsEnabled}
                onChange={(e) => setEditDraft({ ...editDraft, reactionsEnabled: e.target.checked })}
                className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500"
              />
              <span className="text-surface-200 text-sm">Разрешить реакции</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editDraft.membersCanPost}
                onChange={(e) => setEditDraft({ ...editDraft, membersCanPost: e.target.checked })}
                className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-brand-500"
              />
              <span className="text-surface-200 text-sm">Обычные участники могут писать</span>
            </label>

            <div className="flex gap-2 justify-end pt-2">
              <button onClick={() => setEditDraft(null)} className="btn-ghost">Отмена</button>
              <button
                onClick={() => updateMut.mutate(editDraft)}
                disabled={updateMut.isPending || !editDraft.displayName.trim()}
                className="btn-primary"
              >
                {updateMut.isPending ? 'Сохраняем…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
